// -----------------------------
// DONATE PAGE
// -----------------------------
// This page allows visitors to learn about CTrees' mission and make a donation.
// It includes a visual banner, explanation of impact, donation options (via Stripe),
// frequently asked questions, and a newsletter sign-up.

// -----------------------------
// IMPORTS
// -----------------------------
import clsx from "clsx";
import * as components from "@components";
import Stripe from "./components/Stripe";

// (Optional) Meta tag settings for SEO or social sharing
const metaProps = {};

export default function Donate() {
    return (
        <>
            {/* Page metadata for search engines and social previews */}
            <components.MetaTagHeader {...metaProps} />

            <div className={parentStyle}>
                {/* -----------------------------
                    HEADER SECTION (HERO)
                ----------------------------- */}
                {/* Big image and main message */}
                <div className={heroStyle}>
                    <components.HeroOverlay
                        img={
                            "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/donate/DonatePage_Banner.jpg"
                        }
                        gradientRight={false}
                    />
                    <div className="z-20 w-1/2 px-20 text-white">
                        <div className="mb-10 text-lg uppercase tracking-widest">
                            support ctrees
                        </div>
                        <div className="text-4xl">
                            Protect Forests. Track Carbon. Drive Climate Action.
                        </div>
                    </div>
                </div>

                {/* Decorative divider */}
                <components.DottedDivider className="mb-10 mt-20 w-1/4" />

                {/* -----------------------------
                    DONATION INTRO & STRIPE WIDGET
                ----------------------------- */}
                <div className="flex flex-col items-center md:w-[800px]">
                    {/* Short paragraph explaining why donations matter */}
                    <div className={textStyle}>
                        <div className="mb-6 lg:mb-10">
                            Forests are one of our most powerful solutions to
                            climate change, but they’re also increasingly at
                            risk.
                        </div>
                        CTrees provides independent scientific data to
                        governments and organizations to stop deforestation,
                        restore ecosystems, and accelerate nature-based climate
                        solutions on a global scale.
                    </div>

                    {/* Embedded donation interface (one-time or recurring) */}
                    <Stripe />
                </div>

                {/* Decorative divider */}
                <components.DottedDivider className="my-20 w-[90%]" />

                {/* -----------------------------
                    FAQ SECTION
                ----------------------------- */}
                {/* Answers to common questions about donating */}
                <components.FAQ props={questionList} />
            </div>

            {/* -----------------------------
                STAY CONNECTED SECTION
            ----------------------------- */}
            {/* Email sign-up or contact form for future updates */}
            <components.StayConnected />
        </>
    );
}

// -----------------------------
// STYLES
// -----------------------------
const parentStyle = clsx("h-full flex flex-col items-center mb-40");
const heroStyle = clsx("h-[600px] bg-blue flex items-center w-full relative");
const textStyle = clsx(
    "px-8 pb-20 text-center text-xl md:px-10 lg:px-0 lg:text-2xl",
);

// -----------------------------
// FAQ CONTENT
// -----------------------------
// These are the actual questions and answers displayed under the FAQ section.
export const questionList = [
    {
        question: "Is this donation tax deductible?",
        answer: "CTrees is a 501(c)3 tax-exempt organization and your donation is tax-deductible within the guidelines of U.S. law. To claim a donation as a deduction on your U.S. taxes, please keep your email donation receipt as your official record. We'll send it to you upon successful completion of your donation.",
    },
    {
        question: "Who can I talk to with questions about making a gift?",
        answer: "If you have questions, please reach out to our Director of Development, Janet Smith, at (jsmith at ctrees.org).",
    },
    {
        question: "What is CTrees’ Donor Privacy Policy?",
        answer: "CTrees will not sell, share or trade donors' names or personal information with any other entity, nor send mailings to donors on behalf of other organizations. This policy applies to all information received by CTrees, both online and offline, as well as any electronic, written, or oral communications.",
    },
    {
        question: "What is workplace giving?",
        answer: "Many employers offer matching programs to amplify the impact of your generosity to nonprofits such as CTrees. Some organizations even match gifts from spouses, retirees, and part-time employees. Contact your HR department to see if your gifts are eligible for matching and maximize your contributions to CTrees (EIN/Tax ID: 88-0538773).",
    },
];
